import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './HomeOwnerPricing.module.css';

const HomeOwnerPricing = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);

  
  // Get authentication status from localStorage
  const isAuthenticated = !!localStorage.getItem('token');

  const handlePurchase = (product) => {
    if (!isAuthenticated) {
      navigate('/homeowner/login', { state: { from: '/homeowner/upgrade' } });
      return;
    }

    // Calculate the final price based on billing cycle
    const finalPrice = isAnnual ? Math.round(product.price * 12 * 0.8) : product.price;
    const productWithBilling = {
      ...product,
      finalPrice,
      billingCycle: isAnnual ? 'yearly' : 'monthly'
    };

    // Navigate to checkout with product details
    navigate('/homeowner/checkout', {
      state: { selectedPlan: productWithBilling }
    });
  };

  const calculatePrice = (basePrice) => {
    if (isAnnual) {
      return Math.round(basePrice * 12 * 0.8); // 20% discount for annual
    }
    return basePrice;
  };


  const products = [
    {
      id: 1,
      name: 'Essential Analysis',
      description: 'Get a detailed comparison between contractor quotes to identify the best value.',
      price: 29.99,
      features: [
        'AI-powered quote analysis',
        'Price comparison breakdown',
        'Material quality assessment',
        'Timeline evaluation',
        'Value scoring for each quote',
        'Downloadable PDF report',
        'Email support'
      ],
      popular: false,
      buttonText: "Buy Now"
    },
    {
      id: 2,
      name: 'Add-on: Questions Package',
      description: 'Don\'t miss important details. Get expert questions to ask your contractor.',
      price: 14.99,
      features: [
        'Customized questions for your project',
        'Avoid common pitfalls',
        'Uncover hidden costs',
        'Material specifications guidance',
        'Timeline clarification prompts',
        'Printable question checklist'
      ],
      popular: false,
      buttonText: "Buy Now"
    },
    {
      id: 3,
      name: 'Complete Bundle',
      description: 'Everything you need for smart contractor selection with bonus features.',
      price: 49.99,
      features: [
        'Everything in Essential Analysis',
        'Everything in Questions Package',
        'Detailed timeline analysis by phase',
        'Allowance breakdown by category',
        'Scope coverage analysis with pros/cons',
        'File upload & management system',
        'Interactive charts (bar and radar views)',
        'Priority email support',
        'Saved project history'
      ],
      popular: true,
      buttonText: "Buy Now"
    }
  ];

  return (
    <div className={styles.pricingContainer}>
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1>Upgrade Your Renovation Experience</h1>
          <p>Make informed decisions with our professional contractor analysis tools</p>
          
          <div className={styles.pricingToggle}>
            <span className={!isAnnual ? styles.active : ''}>Monthly</span>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={isAnnual}
                onChange={() => setIsAnnual(!isAnnual)}
              />
              <span className={styles.slider}></span>
            </label>
            <span className={isAnnual ? styles.active : ''}>
              Annual
              <span className={styles.discount}>Save 20%</span>
            </span>
          </div>
        </div>
      </div>

      <div className={styles.pricingGrid}>
        {products.map((product) => (
          <div 
            key={product.id} 
            className={`${styles.pricingCard} ${product.popular ? styles.popular : ''}`}
          >
            {product.popular && (
              <div className={styles.popularBadge}>Recommended</div>
            )}
            <div className={styles.planHeader}>
              <h3>{product.name}</h3>
              <p className={styles.description}>{product.description}</p>
            </div>
            <div className={styles.price}>
              <span className={styles.currency}>$</span>
              <span className={styles.amount}>
                {calculatePrice(product.price)}
              </span>
              <span className={styles.period}>
                /{isAnnual ? 'year' : 'month'}
              </span>
              {/* <span className={styles.oneTime}>one-time payment</span> */}
            </div>
            <ul className={styles.features}>
              {product.features.map((feature, index) => (
                <li key={index}>
                  <i className="fas fa-check-circle"></i>
                  {feature}
                </li>
              ))}
            </ul>
            <button 
              className={styles.pricingButton}
              onClick={() => handlePurchase(product)}
            >
              {product.buttonText}
            </button>
          </div>
        ))}
      </div>

      <div className={styles.howItWorks}>
        <h2>How It Works</h2>
        <div className={styles.stepsContainer}>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-upload"></i>
            </div>
            <h3>Upload Your Quotes</h3>
            <p>Upload contractor proposals you've received for your renovation project.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-robot"></i>
            </div>
            <h3>AI Analysis</h3>
            <p>Our system analyzes contracts, identifies key differences and potential issues.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-file-alt"></i>
            </div>
            <h3>Get Your Report</h3>
            <p>Receive a comprehensive report with insights and side-by-side comparisons.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <i className="fas fa-handshake"></i>
            </div>
            <h3>Make Confident Decisions</h3>
            <p>Choose the right contractor with complete peace of mind.</p>
          </div>
        </div>
      </div>

      <div className={styles.testimonials}>
        <h2>What Homeowners Say</h2>
        <div className={styles.testimonialsGrid}>
          <div className={styles.testimonial}>
            <div className={styles.quote}>
              <i className="fas fa-quote-left"></i>
              <p>The comparison report saved me over $5,000 by identifying unnecessary costs in one of my quotes.</p>
              <div className={styles.author}>— Jennifer K., Kitchen Remodel</div>
            </div>
          </div>
          <div className={styles.testimonial}>
            <div className={styles.quote}>
              <i className="fas fa-quote-left"></i>
              <p>The clarification questions guided my conversations with contractors and helped me avoid potential misunderstandings.</p>
              <div className={styles.author}>— Michael T., Bathroom Renovation</div>
            </div>
          </div>
          <div className={styles.testimonial}>
            <div className={styles.quote}>
              <i className="fas fa-quote-left"></i>
              <p>The complete bundle was worth every penny. I felt so much more confident negotiating with contractors knowing I had expert analysis.</p>
              <div className={styles.author}>— Sarah B., Home Addition</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.faqSection}>
        <h2>Frequently Asked Questions</h2>
        <div className={styles.faqGrid}>
          <div className={styles.faqItem}>
            <h3>How long does the analysis take?</h3>
            <p>Most reports are completed within 24 hours of submission. For complex projects with multiple quotes, it may take up to 48 hours.</p>
          </div>
          <div className={styles.faqItem}>
          <h3>Can I cancel my subscription anytime?</h3>
          <p>Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period.</p>
          </div>
          <div className={styles.faqItem}>
            <h3>What file formats do you accept?</h3>
            <p>We accept PDF, DOC, DOCX, JPG, and PNG files. If your quote is in another format, please contact support.</p>
          </div>
          <div className={styles.faqItem}>
            <h3>What if I need help understanding my report?</h3>
            <p>Our support team is available to help you understand any part of your report. Premium bundle customers receive priority support.</p>
          </div>
        </div>
      </div>

      <div className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>Ready to Upgrade Your Renovation Journey?</h2>
          <p>Make informed decisions and get the best value for your home improvement project.</p>
          <div className={styles.ctaButtons}>
            <button onClick={() => handlePurchase(products[2])} className={styles.ctaButton}>
              Get Complete Bundle
            </button>
            <Link to="/homeowner/support" className={styles.ctaButtonSecondary}>
              Have Questions?
            </Link>
          </div>
          <div className={styles.paymentIcons}>
            <i className="fab fa-cc-visa"></i>
            <i className="fab fa-cc-mastercard"></i>
            <i className="fab fa-cc-amex"></i>
            <i className="fab fa-cc-paypal"></i>
            <i className="fab fa-stripe"></i>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeOwnerPricing; 